import { NotificationInterface } from '@partnerconnect-monorepo/types/notification/Notification';
import { t } from '@transifex/native';
import { useUpdateNotification } from '../../../../api/notifications';
import { useSnack } from '../../../../context/snackBar';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { debounce } from 'lodash';

export const useNotification = (
  notification: NotificationInterface,
  module: 'reporting' | 'partnershipsAndFinances'
) => {
  const { handleSubmit, watch, control, reset } =
    useForm<NotificationInterface>({
      defaultValues: notification
    });

  const snack = useSnack();

  const { mutate: updateNotification, isPending: isUpdateNotificationLoading } =
    useUpdateNotification({
      onSuccess: () =>
        snack.success(t('Notification settings updated successfully')),
      onError: () => {
        snack.error(t('Updating notification settings failed'));
        reset(notification);
      }
    });

  const onSubmit = useCallback(
    ({ code, enabled, frequency }: NotificationInterface) =>
      updateNotification({
        code,
        enabled,
        module,
        frequency: frequency.value
      }),
    [module, updateNotification]
  );

  const debounceSubmit = useMemo(
    () => debounce(() => handleSubmit(onSubmit)(), 200),
    [onSubmit, handleSubmit]
  );

  // auto-save
  useEffect(() => {
    const subscription = watch(() => {
      debounceSubmit();
    });

    return () => {
      subscription.unsubscribe();
      debounceSubmit.cancel();
    };
  }, [handleSubmit, watch]);

  return {
    control,
    isLoading: isUpdateNotificationLoading
  };
};
